import { LogoBanner } from "@/components/LogoBanner";
import { PlayErrorBoundary } from "@/components/PlayErrorBoundary";
import { preloadCards } from "@/components/ui/cardSvgUtils";
import { CourtCard } from "@/game/play/courtCard";
import { Face, isPanel } from "@/game/play/face";
import { GameCard } from "@/game/play/gameCard";
import { NewGameButton, PlayControls } from "@/game/play/playControls";
import { cn } from "@/lib/utils";
import { usePresence } from "@/hooks/usePresence";
import { useSyncThemeColorToBackground } from "@/hooks/useSyncThemeColor";
import { useGameStore } from "@/store/gameStore";
import { Courts, SuitType } from "@/store/types";
import React from "react";

const getSuitBgValue = (cardSuit: keyof typeof SuitType | undefined) => {
  const suit = cardSuit ? SuitType[cardSuit] : null;
  switch (suit) {
    case "red":
      return "var(--color-suit-red)";
    case "purple":
      return "var(--color-suit-purple)";
    default:
      return "var(--color-suit-black)";
  }
};

const PlayScreen: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const card = useGameStore((state) => state.currentCard);
  const screenRef = React.useRef<HTMLDivElement>(null);
  useSyncThemeColorToBackground(screenRef);
  const suitVal = getSuitBgValue(card?.suit);

  return (
    <div
      className="bg-suit h-lvh transition-colors duration-500"
      style={
        {
          "--color-suit": suitVal,
        } as React.CSSProperties
      }
      ref={screenRef}
    >
      {children}
    </div>
  );
};

const Play = () => {
  const courts = useGameStore((state) => state.game.courts);
  const hasGame = useGameStore((state) => state.hasGame());
  // What the card at the bottom shows. With no game yet there's nothing to
  // do but set one up.
  const [face, setFace] = React.useState<Face>(() =>
    useGameStore.getState().hasGame() ? "court" : "setup",
  );

  // With no game the card is only there for setup. It shrinks away when
  // setup is cancelled, still showing setup on its way out.
  const cardShown = hasGame || face === "setup";
  const card = usePresence(cardShown, 220);
  const cardFace = React.useRef(face);
  if (cardShown) cardFace.current = face;

  React.useEffect(() => {
    if (courts) {
      const courtCards = courts.map((c) => Courts[c]);
      preloadCards(courtCards);
    }
  }, [courts]);

  return (
    <PlayErrorBoundary>
      <PlayScreen>
        <LogoBanner matchCard className="pt-safe-min-4 fixed" />
        <CourtCard />
        {/* Dims the game behind the open card; a tap anywhere shrinks the
            card back to the court, which cancels setup. */}
        <div
          className={cn(
            "fixed inset-0 bg-black/35 transition-opacity duration-300",
            face === "court" ? "pointer-events-none opacity-0" : "opacity-100",
          )}
          onClick={() => setFace("court")}
          aria-hidden
        />
        <div className="pb-safe-min-4 short:right-0 short:left-auto short:mx-0 short:w-[min(28rem,max(45vw,21rem))] short:pr-safe-min-4 fixed inset-x-0 bottom-0 mx-auto flex max-w-md flex-col px-4">
          {card.mounted ? (
            <div
              className={cn(
                "origin-bottom",
                card.leaving ? "animate-card-out" : "animate-card-in",
              )}
              inert={card.leaving}
            >
              <GameCard face={cardFace.current} setFace={setFace} />
            </div>
          ) : null}
          {/* Faded behind the menu; setup and sharing fill the card and
              take the controls' place. */}
          <div
            className={cn(
              "grid [transition:grid-template-rows_680ms_var(--ease-bouncy),margin_680ms_var(--ease-bouncy),opacity_300ms]",
              isPanel(face)
                ? "mt-0 grid-rows-[0fr] opacity-0"
                : "mt-3 grid-rows-[1fr]",
              face === "menu" && "opacity-30",
            )}
            inert={face !== "court"}
          >
            <div className={cn("min-h-0", isPanel(face) && "overflow-hidden")}>
              {hasGame ? (
                <PlayControls />
              ) : (
                <NewGameButton onClick={() => setFace("setup")} />
              )}
            </div>
          </div>
        </div>
      </PlayScreen>
    </PlayErrorBoundary>
  );
};

export { Play };
